/**
 * client/render/sky.ts — skybox_mp_hijacked_ft cubemap drawn around the camera. OWNER: RENDER agent.
 *
 * [MAP "Sky"] vs_pimp_shader_skycubemaphdr: lookup = (dot(p.xy, rot.yz), dot(p.xy, rot.xy), p.z) with
 * rot = skyBoxRotationSize (220 deg about Z; derivation + test in lighting.ts skyLookupDir). LDR branch:
 * radiance = rgb^2 (the HDR branch rgb/alpha washes out). ESTIMATE: the sky is shown at its authored colour
 * (out = sqrt(rgb^2) = rgb, before the LUT), since the sky's own exposure constant is runtime-only.
 * Faces: sky_face0..5.png in D3D order +X,-X,+Y,-Y,+Z,-Z; D3D and GL cube addressing agree, so the faces
 * are uploaded unflipped and sampled with the raw rotated CoD vector.
 * Not fogged (the horizon haze is painted into the cubemap).
 */
import * as THREE from 'three';
import type { Bo2Uniforms } from './glsl';

const SKY_VERT = /* glsl */ `
out vec3 vDir;
void main() {
  vDir = position;
  vec4 p = projectionMatrix * mat4(mat3(viewMatrix)) * vec4(position, 1.0);
  gl_Position = p.xyww; // on the far plane
}
`;
const SKY_FRAG = /* glsl */ `
precision highp float;
uniform samplerCube uSkyCube;
uniform vec3 uSkyRot;
in vec3 vDir;
void main() {
  vec3 p = normalize(vec3(vDir.x, -vDir.z, vDir.y)); // three -> CoD
  // The two texel rows at the horizon (rows 254-255 of each side face) are a dark seam, and below the horizon
  // the cube holds unrelated content; in game both are hidden behind the ocean's fogged far edge. Our ocean
  // water is translucent, so mirror directions below the horizon (smooth, like a reflection) and keep >= 0.45 deg
  // elevation so the seam rows never show (ESTIMATE).
  p.z = max(abs(p.z), 0.008 * length(p.xy));
  vec3 v = vec3(dot(p.xy, uSkyRot.yz), dot(p.xy, uSkyRot.xy), p.z);
  vec3 c = texture(uSkyCube, v).rgb;
  gl_FragColor = vec4(c, 1.0); // sqrt(rgb^2): authored LDR colour (ESTIMATE, see header)
}
`;

export async function loadSkyCube(base: string, faces: string[]): Promise<THREE.CubeTexture> {
  const imgs = await Promise.all(faces.map(async (f) => {
    const r = await fetch(base + f);
    if (!r.ok) throw new Error(`sky face ${f}: ${r.status}`);
    return createImageBitmap(await r.blob(), { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  }));
  const tex = new THREE.CubeTexture(imgs as unknown as HTMLImageElement[]);
  tex.flipY = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export function createSkyMesh(U: Bo2Uniforms): THREE.Mesh {
  const m = new THREE.ShaderMaterial({
    name: 'bo2_sky',
    uniforms: { uSkyCube: U.uSkyCube, uSkyRot: U.uSkyRot },
    vertexShader: SKY_VERT,
    fragmentShader: SKY_FRAG,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), m);
  mesh.name = 'bo2_sky';
  mesh.frustumCulled = false;
  mesh.renderOrder = 1000; // last among opaque: only background pixels pass the depth test
  return mesh;
}
